import { useMemo } from "react";

import {
  flattenConversationPages,
  useChatConversations,
  useCurrentWorkspace,
  useFeatureflags,
  useOrderMetadata,
  useOrderMutation,
  useOrdersCache,
  useSessionUser,
  useWorkspaceConnections,
} from "@web-app/api";
import {
  Avatar,
  Button,
  CopyableField,
  DateTimeTz,
  Icon,
  getRouteStatusText,
} from "@web-app/components";
import ChatIcon from "@web-app/pages/chatPage/icons/ChatIcon";
import StatusCell from "@web-app/pages/manage/Table/StatusCell";
import useDispatchStore from "@web-app/store/dispatchStore";
import { Order, OrderDeliveryType, TransportType, User } from "@web-app/types";
import {
  generateOrderTrackingLink,
  getProperWsCustomDomainAndId,
  pluralize,
} from "@web-app/utils/helpers";
import { hasExternalAssignee } from "@web-app/utils/order";

import ChecklistSummary from "./ChecklistSummary";

const orderRequirementLabels = {
  itemChecklist: "Item Checklist",
  note: "Add Notes",
  photo: "Photo of Delivery",
  signature: "Customer Signature",
};

export default function OrderSummaryTab({
  assignee,
  order,
}: {
  assignee?: User | null;
  order: Order;
}) {
  const { data: conversationsData } = useChatConversations();
  const { data: sessionUser, isLoading: isSessionUserLoading } =
    useSessionUser();
  const conversations = flattenConversationPages(conversationsData);
  const { hasFeature } = useFeatureflags();
  const hasConversations = hasFeature("conversations");
  const { data: orderMetadata } = useOrderMetadata(order.id);
  const participantUsers = orderMetadata?.participantUsers ?? [];

  const { updateOrder } = useOrderMutation();
  const ordersCache = useOrdersCache();

  // Ticking an item saves the whole list for that leg. web-api records each
  // check/uncheck in the order timeline with who made it.
  const toggleChecklistItem =
    (checklistKey: "dropoffChecklist" | "pickupChecklist") =>
    async (index: number, isChecked: boolean) => {
      const checklist = (order[checklistKey] ?? []).map((item, i) =>
        i === index ? { ...item, isChecked } : item,
      );
      ordersCache.upsertOrderInCache({ ...order, [checklistKey]: checklist });
      try {
        await updateOrder.mutateAsync({
          [checklistKey]: checklist,
          id: order.id,
        });
      } catch {
        // updateOrder already shows the error; put the checkbox back.
        ordersCache.upsertOrderInCache(order);
      }
    };

  const hasPickupRequirements =
    Object.values(order.pickupRequirements ?? {}).filter(Boolean).length > 0;
  const hasDropoffRequirements =
    Object.values(order.dropoffRequirements ?? {}).filter(Boolean).length > 0;

  const vehicleLabel =
    Object.entries(TransportType).find(
      ([, value]) => value === order.minVehicle,
    )?.[0] ?? order.minVehicle;
  const isP2PDelivery =
    order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF;

  const {
    data: workspaceConnections,
    isLoading: isLoadingWorkspaceConnections,
  } = useWorkspaceConnections();

  const { data: currentWorkspace } = useCurrentWorkspace();

  const [orderWsCustomDomain, orderId] = useMemo<string[]>(() => {
    if (!isLoadingWorkspaceConnections && !isSessionUserLoading) {
      const orderWsId = order?.workspaceId;
      const orderConnWsId = order?.connectedWorkspaceId ?? null;
      const orderId = order?.id;

      if (!workspaceConnections || !sessionUser || !orderWsId || !orderId)
        return ["", ""];

      return getProperWsCustomDomainAndId(
        workspaceConnections,
        orderWsId,
        orderConnWsId,
        orderId,
        sessionUser.currentWorkspaceId === order.workspaceId,
        currentWorkspace?.customDomain || "",
      );
    }

    return ["", ""];
  }, [
    workspaceConnections,
    sessionUser,
    currentWorkspace,
    isLoadingWorkspaceConnections,
    isSessionUserLoading,
    order,
  ]);

  const hasStarted =
    !!order.actualStartDateTime ||
    !!order.actualPickupDateTime ||
    !!order.geoPickupDateTime ||
    !!order.geoDropoffDateTime ||
    !!order.actualEndDateTime;

  const handleOpenChat = () => {
    if (!order.assigneeId || !assignee) return;

    const existingConversation = conversations.find(
      (conv) =>
        conv.conversation.orderId === order.id &&
        conv.participantsWithDetails.some((p) => p.userId === order.assigneeId),
    );

    if (existingConversation) {
      useDispatchStore.setState({
        activeChatConversationId: existingConversation.conversation.id,
        activeChatDriverId: order.assigneeId,
        isChatPanelOpen: true,
        pendingChatDriverImage: null,
        pendingChatDriverName: null,
        pendingChatOrderId: null,
        pendingChatUserId: null,
      });
    } else {
      useDispatchStore.setState({
        activeChatConversationId: null,
        activeChatDriverId: order.assigneeId,
        isChatPanelOpen: true,
        pendingChatDriverImage: assignee.image || null,
        pendingChatDriverName: assignee.name,
        pendingChatOrderDisplayId: order.displayId,
        pendingChatOrderId: order.id,
        pendingChatUserId: order.assigneeId,
      });
    }
  };

  return (
    <div className="common-order-summaryTab bg-white flex flex-col pb-10 overflow-y-auto">
      {order.routeId && (
        <div className="flex flex-col gap-2 px-4 pb-5 [&:not(:last-child)]:border-b border-gray-200">
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Route</span>
            <div className="text-gray-700 flex gap-1 flex-col">
              <span className="tw-details-field-value flex gap-2 items-center">
                {order.route?.externalId ?? "N/A"}{" "}
                <span className="w-fit text-[10px] md:text-xs">
                  <StatusCell
                    value={getRouteStatusText(
                      order.route?.status ?? "INCOMPLETE",
                      !!order.route?.assigneeId,
                    )}
                  />
                </span>
              </span>
              <span className="w-fit text-xs"></span>
              <span className="text-xs text-gray-500 flex gap-2">
                <Button
                  icon="fasRoute"
                  size="sm"
                  onClick={() => {
                    useDispatchStore.setState({
                      focusedOrderId: null,
                      focusedRouteId: order.routeId,
                    });
                  }}
                >
                  <span className="text-xs">View Route</span>
                </Button>
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Status</span>
          <span className="tw-details-field-value w-fit !text-[10px] md:!text-xs">
            <StatusCell value={order.statusText} />
          </span>
        </div>

        {order.statusDetails?.note && (
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Driver's Note</span>
            <span className="tw-details-field-value break-words">
              {order.statusDetails.note}
            </span>
          </div>
        )}
        {order.statusDetails?.reason && (
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Reason</span>
            <span className="tw-details-field-value break-words">
              {order.statusDetails.reason}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Scheduled Start Time</span>
          <span className="tw-details-field-value">
            <DateTimeTz
              start={new Date(order.scheduledStartDateTime)}
              timezone={order.timezone}
              presentation="default"
            />
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Scheduled End Time</span>
          <span className="tw-details-field-value">
            <DateTimeTz
              start={new Date(order.scheduledEndDateTime)}
              timezone={order.timezone}
              presentation="default"
            />
          </span>
        </div>
        {order.status !== "COMPLETE" && order.eta && order.eta.dateTime && (
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">
              Estimated Completion Time
            </span>
            <span className="tw-details-field-value">
              <DateTimeTz
                start={new Date(order.eta.dateTime)}
                timezone={order.timezone}
                presentation="default"
              />
            </span>
          </div>
        )}
      </div>

      {hasStarted && (
        <div className="flex flex-col gap-2 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
          {!!order.actualStartDateTime && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">
                In Transit to{" "}
                {order.type.includes("PICKUP") ? "Pickup" : "Drop-off"} At
              </span>
              <span className="tw-details-field-value">
                <DateTimeTz
                  start={new Date(order.actualStartDateTime)}
                  timezone={order.timezone}
                  presentation="default"
                />
              </span>
            </div>
          )}
          {!!order.geoPickupDateTime && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Pickup Arrived At</span>
              <span className="tw-details-field-value">
                <DateTimeTz
                  start={new Date(order.geoPickupDateTime)}
                  timezone={order.timezone}
                  presentation="default"
                />
              </span>
            </div>
          )}
          {!!order.actualPickupDateTime && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">
                {order.status === "CANCELLED" ? "Cancelled" : "Picked Up"} At
              </span>
              <span className="tw-details-field-value">
                <DateTimeTz
                  start={new Date(order.actualPickupDateTime)}
                  timezone={order.timezone}
                  presentation="default"
                />
              </span>
            </div>
          )}
          {!!order.geoDropoffDateTime && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">
                Drop-off Arrived At
              </span>
              <span className="tw-details-field-value">
                <DateTimeTz
                  start={new Date(order.geoDropoffDateTime)}
                  timezone={order.timezone}
                  presentation="default"
                />
              </span>
            </div>
          )}
          {!!order.actualEndDateTime && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">
                {order.status === "CANCELLED" ? "Cancelled" : "Dropped off"} At
              </span>
              <span className="tw-details-field-value">
                <DateTimeTz
                  start={new Date(order.actualEndDateTime)}
                  timezone={order.timezone}
                  presentation="default"
                />
              </span>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-[1fr_2fr] gap-4 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
        <span className="tw-details-field-title">Assignee</span>
        <span className="tw-details-field-value font-semibold flex flex-col gap-2">
          <span className="flex items-center gap-2">
            {!assignee && <span>No Assignee</span>}
            {assignee && (
              <>
                <span>
                  <Avatar image={assignee.image ?? undefined} size={10} />
                </span>
                <span className="flex flex-col">
                  <span className="font-semibold text-gray-700 break-words">
                    {assignee.name ?? ""}
                  </span>
                  <CopyableField
                    icon="fasPhone"
                    value={assignee.phone ?? ""}
                    appearance="inline"
                    className="text-xs font-normal"
                  />
                  {hasConversations && !hasExternalAssignee(order) && (
                    <button
                      onClick={handleOpenChat}
                      className="mt-2 flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <ChatIcon className="w-4 h-4" />
                      Send a message
                    </button>
                  )}
                </span>
              </>
            )}
          </span>
        </span>
      </div>

      <div className="flex flex-col gap-2 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Priority</span>
          <span className="tw-details-field-value flex items-center gap-2">
            <span>{order.priority}</span>
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Order Type</span>
          <span className="tw-details-field-value flex items-center gap-2">
            <Icon
              name={
                order.type === "ROUTE_OPT_PICKUP"
                  ? "rightFromBracket"
                  : order.type === "ROUTE_OPT_DROPOFF"
                    ? "rightToBracket"
                    : "route"
              }
              className="w-3 lg:w-4 h-3 lg:h-4 text-gray-400 font-semibold"
            />
            {order.deliveryTypeText}
          </span>
        </div>
      </div>

      {order.pickup && (
        <div className="flex flex-col gap-2 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
          {isP2PDelivery && (
            <div className="grid pb-2">
              <span className="tw-details-field-title font-semibold">
                Pickup Details:
              </span>
            </div>
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Pickup Name</span>
            <span className="tw-details-field-value">{order.pickup.name}</span>
          </div>
          {order.pickup.businessName && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Business Name</span>
              <span className="tw-details-field-value break-words">
                {order.pickup.businessName}
              </span>
            </div>
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Phone Number</span>
            <span className="tw-details-field-value">{order.pickup.phone}</span>
          </div>
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Address</span>
            <span className="tw-details-field-value break-words">
              {order.pickup.address}
            </span>
          </div>
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Street Address</span>
            <span className="tw-details-field-value break-words">
              {order.pickup.streetAddress || "N/A"}
            </span>
          </div>
          {order.pickup.extendedAddress && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Apt / Suite</span>
              <span className="tw-details-field-value break-words">
                {order.pickup.extendedAddress}
              </span>
            </div>
          )}
          {order.pickup.locality && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">City</span>
              <span className="tw-details-field-value break-words">
                {order.pickup.locality}
              </span>
            </div>
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">State</span>
            <span className="tw-details-field-value break-words">
              {order.pickup.state || "N/A"}
            </span>
          </div>
          {order.pickup.adminArea2 && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">County / District</span>
              <span className="tw-details-field-value break-words">
                {order.pickup.adminArea2}
              </span>
            </div>
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Postal Code</span>
            <span className="tw-details-field-value break-words">
              {order.pickup.postalCode || "N/A"}
            </span>
          </div>
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Country</span>
            <span className="tw-details-field-value break-words">
              {order.pickup.country || "N/A"}
            </span>
          </div>
          {order.pickup.note && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Note</span>
              <span className="tw-details-field-value break-words">
                {order.pickup.note}
              </span>
            </div>
          )}
          {(order.pickupChecklist ?? []).length > 0 && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Checklist</span>
              <div className="tw-details-field-value break-words">
                <ChecklistSummary
                  items={order.pickupChecklist}
                  onToggle={toggleChecklistItem("pickupChecklist")}
                />
              </div>
            </div>
          )}
          {hasPickupRequirements && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Requirements</span>
              <div className="tw-details-field-value break-words">
                {Object.keys(order.pickupRequirements).map((key) => {
                  const typedKey = key as keyof typeof order.pickupRequirements;
                  const value = order.pickupRequirements[typedKey];

                  if (!value) {
                    return null;
                  }

                  return <div>{orderRequirementLabels[typedKey]}</div>;
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {order.dropoff && (
        <div className="flex flex-col gap-2 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
          {isP2PDelivery && (
            <div className="grid pb-2">
              <span className="tw-details-field-title font-semibold">
                Drop-off Details:
              </span>
            </div>
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Drop-off Name</span>
            <span className="tw-details-field-value break-words">
              {order.dropoff.name}
            </span>
          </div>
          {order.dropoff.businessName && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Business Name</span>
              <span className="tw-details-field-value break-words">
                {order.dropoff.businessName}
              </span>
            </div>
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Phone Number</span>
            <span className="tw-details-field-value break-words">
              {order.dropoff.phone}
            </span>
          </div>
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Address</span>
            <span className="tw-details-field-value break-words">
              {order.dropoff.address}
            </span>
          </div>
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Street Address</span>
            <span className="tw-details-field-value break-words">
              {order.dropoff.streetAddress || "N/A"}
            </span>
          </div>
          {order.dropoff.extendedAddress && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Apt / Suite</span>
              <span className="tw-details-field-value break-words">
                {order.dropoff.extendedAddress}
              </span>
            </div>
          )}
          {order.dropoff.locality && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">City</span>
              <span className="tw-details-field-value break-words">
                {order.dropoff.locality}
              </span>
            </div>
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">State</span>
            <span className="tw-details-field-value break-words">
              {order.dropoff.state || "N/A"}
            </span>
          </div>
          {order.dropoff.adminArea2 && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">County / District</span>
              <span className="tw-details-field-value break-words">
                {order.dropoff.adminArea2}
              </span>
            </div>
          )}
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Postal Code</span>
            <span className="tw-details-field-value break-words">
              {order.dropoff.postalCode || "N/A"}
            </span>
          </div>
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            <span className="tw-details-field-title">Country</span>
            <span className="tw-details-field-value break-words">
              {order.dropoff.country || "N/A"}
            </span>
          </div>
          {order.dropoff.note && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Note</span>
              <span className="tw-details-field-value break-words">
                {order.dropoff.note}
              </span>
            </div>
          )}
          {(order.dropoffChecklist ?? []).length > 0 && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Checklist</span>
              <div className="tw-details-field-value break-words">
                <ChecklistSummary
                  items={order.dropoffChecklist}
                  onToggle={toggleChecklistItem("dropoffChecklist")}
                />
              </div>
            </div>
          )}
          {hasDropoffRequirements && (
            <div className="grid grid-cols-[1fr_2fr] gap-4">
              <span className="tw-details-field-title">Requirements</span>
              <div className="tw-details-field-value break-words">
                {Object.keys(order.dropoffRequirements).map((key) => {
                  const typedKey =
                    key as keyof typeof order.dropoffRequirements;
                  const value = order.dropoffRequirements[typedKey];

                  if (!value) {
                    return null;
                  }

                  return <div>{orderRequirementLabels[typedKey]}</div>;
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2 px-4 py-4 [&:not(:last-child)]:border-b  border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">External Reference ID</span>
          <span className="tw-details-field-value">
            {order.externalId || "N/A"}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Order ID</span>
          <span className="tw-details-field-value break-words">{order.id}</span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Description</span>
          <span className="tw-details-field-value break-words">
            {order.details.description || "N/A"}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Item Count</span>
          <span className="tw-details-field-value">
            {order.details.itemsCount}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Service Time</span>
          <span className="tw-details-field-value">
            {order.details.serviceDuration ?? 0}{" "}
            {pluralize("minute", order.details.serviceDuration ?? 0)}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Item Size</span>
          <span className="tw-details-field-value">
            {`Length: ${order.details.length}cm, Width: ${order.details.width}cm, Height: ${order.details.height}cm, Weight: ${order.details.weight}gm`}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Value</span>
          <span className="tw-details-field-value">
            {order.details.currency} {order.details.value || " 0"}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Tip</span>
          <span className="tw-details-field-value">
            {order.details.currency} {order.details.tip || " 0"}
          </span>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          <span className="tw-details-field-title">Mode of Transportation</span>
          <span className="tw-details-field-value">{vehicleLabel}</span>
        </div>
      </div>

      <div className="flex flex-col gap-2 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
        <div className="grid grid-cols-[1fr_2fr] gap-4">
          {orderWsCustomDomain && orderId && (
            <>
              <span className="tw-details-field-title">Tracking Link</span>
              <span
                className="tw-details-field-value"
                style={{ overflowWrap: "anywhere" }}
              >
                <CopyableField
                  value={generateOrderTrackingLink(
                    orderWsCustomDomain,
                    orderId,
                  )}
                  appearance="link-inline"
                  className="text-xs font-normal"
                />
              </span>
            </>
          )}
        </div>
      </div>
      {(order.customFields ?? []).length > 0 && (
        <div className="flex flex-col gap-2 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
          <div className="grid grid-cols-[1fr_2fr] gap-4">
            {order.customFields.map((customField) => {
              if (!customField.values || !customField.values.length) {
                return null;
              }

              const label = customField.definition.name;

              if (customField.definition.type === "DROPDOWN") {
                const selectedValues = customField.values
                  .filter((v) => v.value)
                  .map((v) => {
                    const option = customField.definition.options?.find(
                      (opt) => opt.value === v.value,
                    );
                    return option?.label || String(v.value);
                  });

                if (selectedValues.length === 0) {
                  return null;
                }

                return (
                  <>
                    <span className="tw-details-field-title">{label}</span>
                    <div className="flex flex-wrap gap-1">
                      {selectedValues.map((displayLabel, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-black text-xs"
                        >
                          {displayLabel}
                        </span>
                      ))}
                    </div>
                  </>
                );
              }

              const value = customField.values[0]?.value;
              if (!value) {
                return null;
              }

              return (
                <>
                  <span className="tw-details-field-title">{label}</span>
                  <span className="tw-details-field-value text-wrap">
                    {String(value)}
                  </span>
                </>
              );
            })}
          </div>
        </div>
      )}

      {participantUsers.length > 0 && (
        <div className="grid grid-cols-[1fr_2fr] gap-4 px-4 py-4 [&:not(:last-child)]:border-b border-gray-200">
          <span className="text-sm text-gray-500">Participants</span>
          <div className="flex items-center -space-x-2">
            {participantUsers.map((participant) => (
              <div
                key={participant.id}
                className="relative ring-2 ring-white rounded-full"
                title={participant.name || participant.email}
              >
                <Avatar
                  image={participant.image ?? undefined}
                  name={participant.name || participant.email}
                  size={10}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
